import { Fragment, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AddOperationSplitButtonProps {
  /**
   * Type de la ligne d'ajout ouverte, ou null si aucune ligne n'est en cours de
   * saisie. Non-null = le bouton reste déplié et affiche le type retenu.
   */
  activeType: 'credit' | 'debit' | null;
  /**
   * Appelé au clic sur un segment. Ouvre la ligne d'ajout avec ce type, ou
   * bascule le type de la ligne déjà ouverte.
   */
  onSelect: (type: 'credit' | 'debit') => void;
  className?: string;
  /** Posé sur le conteneur (ciblage du tutoriel). */
  dataTutorial?: string;
}

const TYPES = ['credit', 'debit'] as const;

// Classes écrites en toutes lettres : Tailwind n'émet les règles de
// @layer components que s'il repère leur nom dans les sources.
const SEGMENT_CLASS: Record<(typeof TYPES)[number], string> = {
  credit: 'addsplit__seg--credit',
  debit: 'addsplit__seg--debit',
};

const SEGMENT_TITLE_KEY: Record<(typeof TYPES)[number], string> = {
  credit: 'operations:actions.addCredit',
  debit: 'operations:actions.addDebit',
};

/**
 * Bouton d'ajout d'opération scindé en deux : « + Ajouter » au repos, qui
 * découvre « + Crédit | + Débit » au survol (ou en permanence sur tactile).
 * Choisir le type avant d'ouvrir la ligne évite le toggle dans la colonne
 * montant et supprime les saisies faites avec le mauvais type par défaut.
 *
 * Clavier : le groupe ne consomme qu'un seul arrêt de tabulation (roving
 * tabindex), les flèches ←/→ passent d'un segment à l'autre.
 */
export default function AddOperationSplitButton({
  activeType,
  onSelect,
  className = '',
  dataTutorial,
}: AddOperationSplitButtonProps) {
  const { t } = useTranslation(['operations', 'common']);
  // Segment porteur du tabindex 0 : par défaut celui en cours de saisie.
  const [focusIndex, setFocusIndex] = useState(() => (activeType === 'debit' ? 1 : 0));
  const segRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    let next: number | null = null;
    if (event.key === 'ArrowRight') next = 1;
    else if (event.key === 'ArrowLeft') next = 0;
    if (next === null) return;

    event.preventDefault();
    setFocusIndex(next);
    segRefs.current[next]?.focus();
  };

  return (
    <div
      role="group"
      aria-label={t('operations:actions.addOperationGroup')}
      className={`addsplit${activeType !== null ? ' addsplit--open' : ''} ${className}`}
      onKeyDown={handleKeyDown}
      data-tutorial={dataTutorial}
    >
      {TYPES.map((type, index) => (
        <Fragment key={type}>
          {index > 0 && <span aria-hidden="true" className="addsplit__sep" />}
          <button
            type="button"
            ref={(el) => { segRefs.current[index] = el; }}
            tabIndex={focusIndex === index ? 0 : -1}
            onClick={() => {
              setFocusIndex(index);
              onSelect(type);
            }}
            onFocus={() => setFocusIndex(index)}
            aria-pressed={activeType === null ? undefined : activeType === type}
            className={`addsplit__seg ${
              activeType !== null && activeType !== type
                ? 'addsplit__seg--muted'
                : SEGMENT_CLASS[type]
            }`}
            title={t(SEGMENT_TITLE_KEY[type])}
          >
            <Plus className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{t(`common:operationType.${type}`)}</span>
          </button>
        </Fragment>
      ))}

      {/* Libellé de repos, masqué en CSS dès le survol / focus / saisie */}
      <span aria-hidden="true" className="addsplit__rest">
        <Plus className="w-4 h-4" />
        {t('operations:actions.add')}
      </span>
    </div>
  );
}
